<template>
	<article class="metric_card">
		<header class="metric_head">
			<h4 class="metric_label">{{ label }}</h4>
			<div class="metric_pills">
				<span v-for="pill in pills" :key="pill.text"
					class="pill" :class="`pill--${pill.tone}`">{{ pill.text }}</span>
			</div>
		</header>

		<p class="metric_amount">{{ formatMoney(amount) }}</p>

		<ul v-if="items.length" class="share_list">
			<li v-for="item in items" :key="item.label"
				class="share_row" :class="{ 'share_row--rest': item.isRest }">
				<span class="share_dot" :style="{ background: item.color }"></span>
				<span class="share_label"
					v-tooltip.top="item.label.length > 14 ? item.label : ''">
					{{ item.label }}
				</span>
				<span class="share_track">
					<span class="share_fill"
						:class="{ 'share_fill--negative': item.percent < 0 }"
						:style="{ width: barWidth(item), backgroundColor: item.color }"></span>
				</span>
				<span class="share_value" :class="{ 'is_negative': item.percent < 0 }">
					{{ item.percent }}%
				</span>
			</li>
		</ul>
	</article>
</template>

<script>
import { parseToMoney } from "@/utils/currency";

// Card de un escalón del drawer "Análisis de Categorías" (Rentabilidad):
// monto del mes, sus badges y cómo se reparte entre los ítems de la categoría
// (top 3 + el resto acumulado, que el backend ya recorta).
//
// No reusa `CardVentas` a propósito: esa card es la de los tabs (panel gris
// interior + barras colgando debajo), y acá las cuatro cards son una cascada
// que se lee en fila — el monto y su reparto tienen que ser UN objeto. El
// color de cada ítem es el mismo que su línea en la evolución de abajo, así
// el punto de la card y la línea del gráfico se leen como la misma tienda.
export default {
	name: "CategoryMetricCard",

	props: {
		// Escalón: "Ventas", "Margen Bruto", …
		label: { type: String, default: "" },
		amount: { type: Number, default: 0 },
		// Badges ya formateados: [{ text, tone: "accent"|"positive"|"negative" }].
		pills: { type: Array, default: () => [] },
		// Reparto del monto: [{ label, percent, color, isRest }]. `percent` es
		// entero y SIGNADO (un ítem que resta conserva su signo).
		items: { type: Array, default: () => [] },
	},

	methods: {
		formatMoney(value) {
			return parseToMoney(value);
		},
		// La barra mide la MAGNITUD: con un share negativo un `width` negativo
		// es CSS inválido y la barra desaparecía, dejando sólo el "-12 %"
		// suelto. El signo lo dice el número, en rojo.
		barWidth(item) {
			return `${Math.min(Math.abs(item.percent), 100)}%`;
		},
	},
};
</script>

<style scoped lang="scss">
// Un solo objeto: cifra y reparto en la misma superficie, separados por una
// hairline. Sin panel gris interior — el box-in-box dejaba las barras
// colgando fuera de la card.
.metric_card {
	display: flex;
	flex-direction: column;
	padding: 16px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 14px;
	box-shadow: var(--report-shadow-card);
	min-width: 0;
}

.metric_head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-height: 24px;
}

.metric_label {
	margin: 0;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: var(--report-text-muted-strong);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.metric_pills {
	display: flex;
	gap: 6px;
	flex-shrink: 0;
}

.pill {
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	border: 1px solid transparent;

	&--accent {
		background: var(--report-accent-bg);
		color: var(--report-accent-text);
		border-color: var(--report-accent-border);
	}

	// Los deltas usan el semáforo de variaciones del reporte, no el cyan de
	// los badges neutros: un MoM que cae tiene que leerse como caída.
	&--positive {
		background: var(--report-positive-bg);
		color: var(--report-positive);
		border-color: var(--report-positive-border);
	}

	&--negative {
		background: var(--report-negative-bg);
		color: var(--report-negative);
		border-color: var(--report-negative-border);
	}
}

// Cifras tabulares: los montos de las cuatro cards se alinean dígito con
// dígito a lo largo de la fila.
.metric_amount {
	margin: 6px 0 0;
	font-size: 26px;
	line-height: 1.15;
	font-weight: 700;
	color: var(--report-text);
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.share_list {
	margin: 14px 0 0;
	padding: 12px 0 0;
	border-top: 1px solid var(--report-divider-soft);
	list-style: none;
}

// dot · nombre · barra · %. La columna del nombre es `auto`: en una grilla
// las columnas son compartidas por todas las filas, así los nombres se
// alinean entre sí y la barra se queda con TODO el resto (con `1fr` los
// nombres cortos —"033"— dejaban un hueco muerto antes de la barra). La
// columna del % es fija para que los números caigan en la misma vertical,
// fila a fila y card a card.
.share_row {
	display: grid;
	grid-template-columns: 8px auto minmax(56px, 1fr) 38px;
	align-items: center;
	gap: 8px;
	padding: 3px 0;
}

// El resto acumulado no es un ítem del ranking: va separado por una hairline
// punteada y en gris, para que no se lea como una tienda más.
.share_row--rest {
	margin-top: 4px;
	padding-top: 7px;
	border-top: 1px dashed var(--report-border);

	.share_label {
		color: var(--report-text-muted);
	}
}

.share_dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
}

// Tope de la columna del nombre: un ítem de nombre largo trunca en vez de
// comerse la barra (el tooltip muestra el nombre completo).
.share_label {
	max-width: 130px;
	font-size: 13px;
	font-weight: 500;
	color: var(--report-text-strong-2);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.share_track {
	height: 8px;
	border-radius: 999px;
	background: var(--report-progress-bg);
	overflow: hidden;
}

.share_fill {
	display: block;
	height: 100%;
	border-radius: 999px;
	transition: width 0.3s ease-in-out;
}

// Un ítem que RESTA conserva su color (es la misma tienda que en el gráfico),
// pero va rayado: el largo dice cuánto pesa y la trama, que pesa en contra.
.share_fill--negative {
	background-image: repeating-linear-gradient(
		-45deg,
		rgba(255, 255, 255, 0.65) 0 2px,
		transparent 2px 5px
	);
}

.share_value {
	font-size: 12px;
	font-weight: 600;
	text-align: right;
	color: var(--report-text-muted-strong);
	font-variant-numeric: tabular-nums;

	&.is_negative {
		color: var(--report-negative);
	}
}

@media (prefers-reduced-motion: reduce) {
	.share_fill {
		transition: none;
	}
}
</style>
